import type { Persona, PrototypeBootstrap } from "@peer-start/platform-contracts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ProductTour } from "./ProductTour.js";
import { hasCompletedProductTour, isProductTourDismissedForSession } from "./product-tour-storage.js";
import type { ProductTourView } from "./product-tour-steps.js";

function pathMatches(pathname: string, search: URLSearchParams, pathWithQuery: string): boolean {
  const target = new URL(pathWithQuery, "http://local.invalid");
  if (pathname !== target.pathname) return false;
  for (const [key, value] of target.searchParams.entries()) {
    if (search.get(key) !== value) return false;
  }
  return true;
}

export function EmployeeProductTourHost({
  bootstrap,
  persona,
}: {
  bootstrap: PrototypeBootstrap;
  persona: Persona;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useSearchParams();
  const wantsTour = search.get("tour") === "1";
  const [tourOpen, setTourOpen] = useState(false);
  const view: ProductTourView = search.get("view") === "route" ? "route" : "week";
  const role =
    persona.kind === "employee" ? bootstrap.roles.find((entry) => entry.id === persona.roleId) : undefined;
  const tourDone = persona.kind === "employee" && hasCompletedProductTour(persona.id);

  useEffect(() => {
    if (persona.kind !== "employee" || !persona.onboardingComplete) return;
    // Never reopen a finished tour from a stale `?tour=1` history entry.
    if (hasCompletedProductTour(persona.id)) {
      if (wantsTour) {
        const nextSearch = new URLSearchParams(search);
        nextSearch.delete("tour");
        setSearch(nextSearch, { replace: true });
      }
    } else if (wantsTour) {
      setTourOpen(true);
    }
    if (location.pathname === "/me" && !isProductTourDismissedForSession(persona.id)) setTourOpen(true);
  }, [location.pathname, persona, wantsTour, search, setSearch]);

  const closeTour = useCallback(() => {
    setTourOpen(false);
    navigate("/me", { replace: true });
  }, [navigate]);

  const requestView = useCallback(
    (nextView: ProductTourView) => {
      if (location.pathname !== "/me") {
        navigate(`/me?view=${nextView}${wantsTour && !tourDone ? "&tour=1" : ""}`, { replace: true });
        return;
      }
      if (search.get("view") === nextView || (nextView === "week" && !search.get("view"))) return;
      setSearch(
        (previous) => {
          const nextSearch = new URLSearchParams(previous);
          nextSearch.set("view", nextView);
          return nextSearch;
        },
        { replace: true },
      );
    },
    [location.pathname, navigate, search, setSearch, tourDone, wantsTour],
  );

  const requestPath = useCallback(
    (pathWithQuery: string) => {
      if (pathMatches(location.pathname, search, pathWithQuery)) return;
      const target = new URL(pathWithQuery, window.location.origin);
      // Keep the post-onboarding tour flag while moving between tour pages.
      if (tourOpen && wantsTour && !tourDone) target.searchParams.set("tour", "1");
      navigate(`${target.pathname}${target.search}`, { replace: true });
    },
    [location.pathname, navigate, search, tourDone, tourOpen, wantsTour],
  );

  if (persona.kind !== "employee" || !persona.onboardingComplete) return null;

  return (
    <ProductTour
      employeeId={persona.id}
      onClose={closeTour}
      onRequestPath={requestPath}
      onRequestView={requestView}
      open={tourOpen}
      roleTitle={role?.title ?? t("common.employee")}
      view={view}
    />
  );
}
